import { useMassActionsContext } from '@components/MassActions/contexts/MassActions.context';
import { useCallback, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import LangEnum from '../../../../../lang/LangEnum';
import snackbarStore from '../../../../../stores/Snackbar.store';
import { UsedInOfflineMode } from '../../../../offline/types/UsedInOfflineMode';
import { TranslateType } from '../../../../translation/translationContext.types';
import { OrderActionProps } from '../../../actions/types/orderActionsContext.types';
import {
  CsvTemplate,
  ExportFormat,
  ExportOrderForm,
  ExportOrderScope,
} from '../ExportOrderModal.types';
import {
  useExportOrdersCsvMutation,
  useExportOrdersPdfMutation,
} from '../services/exportOrder.mutations';
import {
  defaultOrderFilename,
  filledModelOrderFilename,
  loadExportOrderConfigs,
  parseGroupByEnumPrefix,
  saveExportOrderConfigs,
} from '../utils/exportOrder.utils';
import { exportOrderModelXlsx } from '../utils/exportOrderModelXlsx';

interface UseExportOrderParams extends UsedInOfflineMode {
  exportScope: ExportOrderScope;
  onClose: () => void;
  orderRaw?: OrderActionProps;
  translate: TranslateType;
  openVerupModal?: () => void;
  hasVerupIntegration: boolean;
}

export const useExportOrder = ({
  isOfflineMode = false,
  exportScope,
  orderRaw,
  translate,
  hasVerupIntegration,
  openVerupModal,
  onClose,
}: UseExportOrderParams) => {
  const pdfMutation = useExportOrdersPdfMutation(exportScope.isSyncExport, () => onClose());
  const csvMutation = useExportOrdersCsvMutation(isOfflineMode, () => onClose());
  const { filterMaxResults } = useMassActionsContext();
  const [isDownloadingModel, setIsDownloadingModel] = useState(false);

  const formMethods = useForm<ExportOrderForm>({
    defaultValues: loadExportOrderConfigs(isOfflineMode),
    mode: 'onChange',
  });

  const { setValue, handleSubmit, control } = formMethods;

  const formatWatch = useWatch({ control, name: 'format' });
  const tabContent = isOfflineMode ? ExportFormat.CSV : formatWatch;
  const setTabContent = useCallback(
    (format: ExportFormat) => {
      if (isOfflineMode) {
        return;
      }
      setValue('format', format, { shouldDirty: true, shouldTouch: true });
    },
    [isOfflineMode, setValue],
  );

  const filename = defaultOrderFilename({
    isOffline: isOfflineMode,
    sufix: (exportScope.isSyncExport && orderRaw?.orderCode) || '',
    customerCommercialName: exportScope.isSyncExport ? orderRaw?.customerCommercialName : undefined,
    translate,
  });

  const submit = handleSubmit(async (formValues: ExportOrderForm) => {
    const { groupBy, ...rest } = formValues;
    const isModelExport = !isOfflineMode && rest.csvTemplate === CsvTemplate.FILLED_MODEL;
    const csvTemplateToPersist =
      rest.csvTemplate === CsvTemplate.FILLED_MODEL ? CsvTemplate.DEFAULT : rest.csvTemplate;
    saveExportOrderConfigs({
      ...formValues,
      csvTemplate: csvTemplateToPersist,
      format: tabContent,
    });

    if (tabContent === ExportFormat.PDF) {
      return pdfMutation.mutate({
        filename,
        isSyncScope: exportScope.isSyncExport,
        selectedTab: exportScope.selectedTab,
        options: { ...parseGroupByEnumPrefix(groupBy), ...rest },
        scope: exportScope.scope,
        translate,
        brandId: orderRaw?.brandId,
      });
    }

    if (isModelExport) {
      try {
        setIsDownloadingModel(true);
        await exportOrderModelXlsx({
          exportScope,
          fileName: `${filledModelOrderFilename(translate)}.xlsx`,
        });
        onClose();
      } catch {
        snackbarStore.handleMessage({
          type: 'error',
          message: translate(LangEnum.GLOBAL_EXPORT_ERROR),
        });
      } finally {
        setIsDownloadingModel(false);
      }
      return;
    }

    if (hasVerupIntegration && openVerupModal) {
      return openVerupModal();
    }

    csvMutation.mutate({
      filename,
      exportScope,
      translate,
      brandId: orderRaw?.brandId!,
      selectedTab: exportScope.selectedTab,
      filterMaxResults,
    });
  });

  return {
    control,
    tabContent,
    setTabContent,
    submit,
    formMethods,
    isSubmitting: pdfMutation.isPending || csvMutation.isPending || isDownloadingModel,
  };
};
